import React, { useState, useEffect } from 'react';
import { Activity, Droplets, RefreshCw, Upload, Wheat, X } from 'lucide-react';
import Card from '../components/ui/Card';
import ApiService from '../services/apiService';
import { fileToBase64, compressImageToCanvas } from '../utils/helpers';
import DiagnosticsPanel from '../components/DiagnosticsPanel';
import GlobalContextBar from '../components/GlobalContextBar';
import TranslationService from '../services/translationService';
import ValidatedInput from '../components/ui/ValidatedInput';
import { commitModule } from '../utils/reportMarkers';
import { parseCropRecommendations } from '../utils/aiParsers';
import { capDataUrl } from '../utils/imageGuards';

const CropRecommendView = ({ t, userId, isRTL, setReportData, selectedDevice }) => {
  const [soilFile, setSoilFile] = useState(null);
  const [soilPreview, setSoilPreview] = useState(null);
  const [soilType, setSoilType] = useState("");
  const [loadingSoil, setLoadingSoil] = useState(false);
      
  const [formData, setFormData] = useState({
      water: "mod",
      N: 0, P: 0, K: 0, temp: 25, hum: 50, ph: 6.5
  });

  const [crops, setCrops] = useState([]);
  const [loadingCrops, setLoadingCrops] = useState(false);
  
  const [globalContext, setGlobalContext] = useState({});
  
  const [streamContent, setStreamContent] = useState("");
  const [thinkingContent, setThinkingContent] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [soilStreamContent, setSoilStreamContent] = useState("");
  const [soilThinkingContent, setSoilThinkingContent] = useState("");
  const [isSoilThinking, setIsSoilThinking] = useState(false);
  const [isSoilComplete, setIsSoilComplete] = useState(false);

  // Instant Client-Side Translation for language toggles
  useEffect(() => {
    if (isComplete && streamContent && !isStreaming) {
        const lang = isRTL ? 'ar' : 'en';
        setIsStreaming(true); // show brief shimmering animation
        TranslationService.translateText(streamContent, lang)
            .then(translated => {
                setStreamContent(translated);
            })
            .catch(err => console.error("Translation error", err))
            .finally(() => setIsStreaming(false));
            
        // translate thinking block as well
        if (thinkingContent) {
            TranslationService.translateText(thinkingContent, lang)
                .then(translated => setThinkingContent(translated))
                .catch(err => console.error(err));
        }
    }
  }, [isRTL]);


  // 1. Soil Analysis
  const handleSoilUpload = async () => {
    if (!soilFile) return;
    setLoadingSoil(true);
    setSoilStreamContent("");
    setSoilThinkingContent("");
    setIsSoilThinking(true);
    setIsSoilComplete(false);

    try {
      const base64Image = await compressImageToCanvas(soilFile, 1);
      
      const payload = {
        image_base64: base64Image,
        lang: isRTL ? 'ar' : 'en',
        stream: true
      };
      if (userId) payload.user_id = userId;

      window.dispatchEvent(new CustomEvent('credit-deducted', { detail: { amount: 5 } }));
      
      const response = await fetch(`${ApiService.BASE_URL}/classify-soil`, {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
          },
          body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error("Failed to connect to AI engine");

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      
      let isReading = true;
      let inThinkBlock = false;
      let cleanContent = "";
      let sseBuffer = "";

      while (isReading) {
          const { done, value } = await reader.read();
          if (done) {
              isReading = false;
              break;
          }

          sseBuffer += decoder.decode(value, { stream: true });
          const lines = sseBuffer.split('\n');
          sseBuffer = lines.pop() || "";
          
          for (const line of lines) {
              if (line.startsWith('data: ')) {
                  const dataStr = line.substring(6);
                  if (dataStr.trim() === '[DONE]') {
                      isReading = false;
                      break;
                  }
                  
                  try {
                      const dataObj = JSON.parse(dataStr);
                      if (dataObj.error) continue;

                      const delta = dataObj.choices?.[0]?.delta;
                      
                      if (delta?.reasoning) {
                          setSoilThinkingContent(prev => prev + delta.reasoning);
                      } 
                      else if (delta?.content) {
                          let contentText = delta.content;
                          
                          if (contentText.includes('<think>')) {
                              inThinkBlock = true;
                              const parts = contentText.split('<think>');
                              if (parts[0]) {
                                  setSoilStreamContent(prev => prev + parts[0]);
                                  cleanContent += parts[0];
                              }
                              if (parts[1]) setSoilThinkingContent(prev => prev + parts[1]);
                          } else if (contentText.includes('</think>')) {
                              inThinkBlock = false;
                              setIsSoilThinking(false);
                              const parts = contentText.split('</think>');
                              if (parts[0]) setSoilThinkingContent(prev => prev + parts[0]);
                              if (parts[1]) {
                                  setSoilStreamContent(prev => prev + parts[1]);
                                  cleanContent += parts[1];
                              }
                          } else {
                              if (inThinkBlock) {
                                  setSoilThinkingContent(prev => prev + contentText);
                              } else {
                                  setSoilStreamContent(prev => prev + contentText);
                                  cleanContent += contentText;
                              }
                          }
                      }
                  } catch (e) { }
              }
          }
      }
      setIsSoilComplete(true);
      setIsSoilThinking(false);

      // --- Structured soil classification (report contract v2, section 6.1) ---
      // The visible stream above is prose. Fire one non-streaming call to obtain the
      // structured { soil_type, confidence } result the PDF soil section is gated on.
      let structuredSoil = null;
      try {
        const structRes = await fetch(`${ApiService.BASE_URL}/classify-soil`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
            },
            body: JSON.stringify({ ...payload, stream: false })
        });
        if (structRes.ok) {
            const structJson = await structRes.json();
            // The endpoint answers HTTP 200 with an apology + confidence 0 on total failure.
            if (structJson && structJson.soil_type && Number(structJson.confidence) > 0) {
                structuredSoil = {
                    soil_type: String(structJson.soil_type).trim(),
                    confidence: Number(structJson.confidence)
                };
            }
        }
      } catch (structErr) {
        console.error("Structured soil classification failed", structErr);
      }

      const soilResult = structuredSoil || { soil_type: cleanContent.trim().slice(0, 160), confidence: 0 };

      // Value forwarded as payload.soil_type to /recommend-crops (untruncated).
      setSoilType(soilResult.soil_type);

      let cappedSoilImage = null;
      try {
        cappedSoilImage = await capDataUrl(base64Image);
      } catch (imgErr) {
        console.error("Soil image cap failed", imgErr);
        cappedSoilImage = null;
      }

      if (setReportData) {
        commitModule(setReportData, 'soil', {
          soil_classification: soilResult,
          soil_input_image_base64: cappedSoilImage || null,
          soil_ai_text: cleanContent
        }, Boolean(soilResult.soil_type), null);
      }

    } catch (e) {
      if (setReportData) {
        commitModule(setReportData, 'soil', {}, false, String(e && e.message ? e.message : e).slice(0, 200));
      }
      alert(`Error: ${e.message}`);
    } finally {
      setLoadingSoil(false);
    }
  };
  
  // Clear image function
  const clearSoilImage = () => {
      setSoilFile(null);
      setSoilPreview(null);
      setSoilType("");
  };

  const handleAutoFill = async () => {
    if (!selectedDevice) {
        alert(isRTL ? "يرجى تحديد جهاز استشعار من لوحة القيادة أولاً" : "Please select an IoT device from the Dashboard first");
        return;
    }
    try {
        const res = await ApiService.get(`/iot-history/${selectedDevice.id}?limit=1`);
        if(res.status === 'success' && res.data && res.data.length > 0) {
            const s = res.data[0];
            setFormData(prev => ({
                ...prev,
                N: s.nitrogen || 0,
                P: s.phosphorus || 0,
                K: s.potassium || 0,
                temp: s.temperature || 0,
                hum: s.humidity || 0,
                ph: s.ph || 0
            }));
        } else {
            alert(isRTL ? "لا توجد بيانات قراءة لهذا الجهاز" : "No live data available for this device");
        }
    } catch(e) { console.error(e); }
  };

  const handleRecommendation = async () => {
    // Soil image is now optional — falls back to globalContext or "Not specified"
    const effectiveSoilType = soilType || globalContext.soilType || "Not specified";

    setLoadingCrops(true);
    setIsStreaming(true);
    setIsThinking(true);
    setStreamContent("");
    setThinkingContent("");
    setIsComplete(false);
    setHasError(false);

    const payload = {
      user_id: userId,
      N: formData.N,
      P: formData.P,
      K: formData.K,
      temperature: formData.temp,
      humidity: formData.hum,
      ph: formData.ph,
      rainfall: parseInt(globalContext.waterQuantity || 100),
      soil_type: effectiveSoilType, 
      lang: isRTL ? 'ar' : 'en',
      stream: true,
      context: globalContext
    };

    try {
      window.dispatchEvent(new Event('refresh-credits'));
      
      const response = await fetch(`${ApiService.BASE_URL}/recommend-crops`, {
          method: 'POST',
          headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
          },
          body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error("Failed to connect to AI engine");

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      
      let isReading = true;
      let inThinkBlock = false;
      // Local accumulators: streamContent is a stale closure inside this async handler.
      let fullText = "";
      let streamErr = null;
      let sseBuffer = "";

      while (isReading) {
          const { done, value } = await reader.read();
          if (done) {
              isReading = false;
              break;
          }

          sseBuffer += decoder.decode(value, { stream: true });
          const lines = sseBuffer.split('\n');
          sseBuffer = lines.pop() || "";
          
          for (const line of lines) {
              if (line.startsWith('data: ')) {
                  const dataStr = line.substring(6);
                  if (dataStr.trim() === '[DONE]') {
                      isReading = false;
                      break;
                  }
                  
                  try {
                      const dataObj = JSON.parse(dataStr);
                      if (dataObj.error) {
                         setHasError(true);
                         setErrorMessage(dataObj.error.message);
                         streamErr = String(dataObj.error.message || 'AI stream error').slice(0, 200);
                         continue;
                      }

                      const delta = dataObj.choices?.[0]?.delta;
                      
                      // Handle Reasoning tokens
                      if (delta?.reasoning) {
                          setThinkingContent(prev => prev + delta.reasoning);
                      } 
                      // Handle Content tokens
                      else if (delta?.content) {
                          let contentText = delta.content;
                          
                          if (contentText.includes('<think>')) {
                              inThinkBlock = true;
                              const parts = contentText.split('<think>');
                              if (parts[0]) { setStreamContent(prev => prev + parts[0]); fullText += parts[0]; }
                              if (parts[1]) setThinkingContent(prev => prev + parts[1]);
                          } else if (contentText.includes('</think>')) {
                              inThinkBlock = false;
                              setIsThinking(false);
                              const parts = contentText.split('</think>');
                              if (parts[0]) setThinkingContent(prev => prev + parts[0]);
                              if (parts[1]) { setStreamContent(prev => prev + parts[1]); fullText += parts[1]; }
                          } else {
                              if (inThinkBlock) {
                                  setThinkingContent(prev => prev + contentText);
                              } else {
                                  setStreamContent(prev => prev + contentText);
                                  fullText += contentText;
                              }
                          }
                      }
                  } catch (e) {
                      // Silently ignore parse errors for partial chunks
                  }
              }
          }
      }
      setIsComplete(true);
      setIsThinking(false);

      let cropRecs = [];
      try {
        cropRecs = (parseCropRecommendations(fullText) || []).slice(0, 5);
      } catch (parseErr) {
        console.error("Crop recommendation parse failed", parseErr);
        cropRecs = [];
      }

      if (setReportData) {
        commitModule(setReportData, 'crop', {
          crop_inputs: payload,
          crop_recommendations: cropRecs,
          crop_ai_text: fullText
        }, !streamErr && Boolean(fullText.trim()), streamErr);
      }
    } catch (e) {
      console.error(e);
      setHasError(true);
      setErrorMessage(e.message);
      if (setReportData) {
        commitModule(setReportData, 'crop', { crop_inputs: payload },
          false, String(e && e.message ? e.message : e).slice(0, 200));
      }
    } finally {
      setLoadingCrops(false);
      setIsStreaming(false);
    }
  };

  // Configuration for the Environmental Data Grid (Exact style matching Yield Tab)
  const envFields = [
    { label: t.crops.inputs.n, key: "N", color: "text-emerald-400" },
    { label: t.crops.inputs.p, key: "P", color: "text-amber-400" },
    { label: t.crops.inputs.k, key: "K", color: "text-purple-400" },
    { label: t.crops.inputs.temp, key: "temp", color: "text-orange-400" }, // key matches formData
    { label: t.crops.inputs.hum, key: "hum", color: "text-cyan-400" },     // key matches formData
    { label: t.crops.inputs.ph, key: "ph", color: "text-pink-400", step: "0.1" },
  ];

  return (
    <div className="h-full flex flex-col max-w-7xl mx-auto font-sans text-[#1B263B] dark:text-white pb-10">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between w-full mb-6">
        <div>
           <h2 className="text-3xl font-bold flex items-center gap-3 text-[#1B263B] dark:text-white">
             <Wheat className="text-[#6B8F71]" size={32} /> {t.crops.title || "Crop Recommendation"}
           </h2>
           <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Discover optimal crop pairings based on soil analytics and environmental factors.</p>
        </div>
      </div>

      <div className="mb-6">
        <GlobalContextBar t={t} isRTL={isRTL} onContextChange={setGlobalContext} hideCropFields={true} />
      </div>

      <div className="flex flex-col lg:flex-row gap-8 w-full flex-1">
        {/* LEFT: INPUTS */}
        <div className="w-full lg:w-1/3 flex flex-col gap-6">
          
          {/* Step 1: Soil Analysis (Drag & Drop) */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-[#1B263B]/5 dark:border-slate-700 shadow-sm transition-colors">
             <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-lg text-[#1B263B] dark:text-white">{t.crops.step1 || "Soil Origin"}</h3>
                {soilPreview && !loadingSoil && (
                    <button onClick={clearSoilImage} className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1 font-medium bg-red-50 px-2 py-1 rounded">
                        <X size={14}/> Clear
                    </button>
                )}
             </div>

             {!soilPreview ? (
                 <div className="relative w-full min-h-[160px] border-2 border-dashed border-[#6B8F71]/50 bg-[#6B8F71]/5 hover:bg-[#6B8F71]/10 dark:bg-slate-900 dark:hover:bg-slate-800 rounded-xl transition-colors cursor-pointer flex flex-col items-center justify-center group overflow-hidden">
                    <input type="file" onChange={(e) => {
                        if(e.target.files[0]) {
                            setSoilFile(e.target.files[0]);
                            setSoilPreview(URL.createObjectURL(e.target.files[0]));
                        }
                    }} className="absolute inset-0 opacity-0 cursor-pointer z-10" />
                    <Upload size={28} className="text-[#6B8F71] group-hover:scale-110 transition-transform mb-2"/>
                    <p className="text-sm text-[#1B263B] dark:text-white font-medium">{t.crops.uploadLabel || "Upload Soil Image"}</p>
                 </div>
             ) : (
                 <div className="relative rounded-xl overflow-hidden border border-[#1B263B]/10 dark:border-slate-700 bg-gray-50 h-[160px] shadow-inner">
                    <img src={soilPreview} alt="Soil" className="w-full h-full object-cover" />
                    
                    {loadingSoil ? (
                        <div className="absolute inset-0 bg-white/40 z-10">
                            <div className="absolute top-0 left-0 w-full h-1 bg-[#C47A2C] shadow-[0_0_15px_rgba(196,122,44,0.8)] animate-scan-laser"></div>
                            <div className="absolute inset-0 flex items-center justify-center backdrop-blur-sm">
                                <div className="bg-[#1B263B]/90 px-4 py-2 rounded-full border border-[#1B263B] flex items-center gap-2 shadow-lg">
                                    <span className="w-2 h-2 bg-[#C47A2C] rounded-full animate-pulse"></span>
                                    <span className="text-white font-mono text-xs tracking-widest">{t.crops.scanning || "SCANNING"}</span>
                                </div>
                            </div>
                        </div>
                    ) : (
                        !soilType && (
                            <div className="absolute inset-0 flex items-center justify-center bg-[#1B263B]/20 backdrop-blur-[2px]">
                                <button onClick={handleSoilUpload} className="bg-[#6B8F71] hover:bg-[#527056] text-white px-5 py-2 rounded-full font-bold shadow-lg transform hover:scale-105 transition-all text-sm">
                                    {t.crops.detectBtn || "Identify Soil"}
                                </button>
                            </div>
                        )
                    )}

                    {soilType && !loadingSoil && !soilStreamContent && (
                        <div className="absolute bottom-0 left-0 right-0 bg-[#1B263B]/90 border-t border-[#1B263B]/10 p-3 flex justify-between items-center backdrop-blur-md">
                            <span className="text-[#6B8F71] text-xs font-bold uppercase tracking-wider">{t.crops.detected || "Detected"}</span>
                            <span className="text-white font-bold">{t.crops.soil_types?.[soilType] || soilType}</span>
                        </div>
                    )}
                 </div>
             )}
             
             {/* Soil Diagnostics Output */}
             {(soilStreamContent || soilThinkingContent) && (
                <div className="mt-4">
                  <DiagnosticsPanel
                    isStreaming={loadingSoil}
                    streamContent={soilStreamContent}
                    thinkingContent={soilThinkingContent}
                    isThinking={isSoilThinking}
                    isComplete={isSoilComplete}
                    hasError={false}
                    errorMessage=""
                    onRetry={handleSoilUpload}
                    onNewAnalysis={() => { setSoilStreamContent(''); setSoilThinkingContent(''); setIsSoilComplete(false); }}
                    t={t}
                    isRTL={isRTL}
                  />
                </div>
             )}
          </div>

          {/* Step 2: Environmental Data */}
          <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-[#1B263B]/5 dark:border-slate-700 shadow-sm flex-1 flex flex-col transition-colors">
             <div className="flex justify-between items-center mb-5">
                <h3 className="font-bold text-lg text-[#1B263B] dark:text-white flex items-center gap-2">
                   <Activity size={18} className="text-[#C47A2C]"/> {t.crops.step2 || "Local Environment"}
                </h3>
                <button onClick={handleAutoFill} className="text-xs bg-[#6B8F71]/10 hover:bg-[#6B8F71]/20 text-[#6B8F71] px-3 py-1.5 rounded-full flex items-center gap-2 transition-all font-bold">
                   <RefreshCw size={12} className={loadingCrops ? "animate-spin" : ""} /> {t.crops.autoFill || "Auto-fill"}
                </button>
             </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                 {envFields.map((field) => (
                     <div key={field.key} className="flex flex-col">
                         <label className="text-[10px] uppercase tracking-wider font-bold text-gray-500 dark:text-gray-400 mb-1 ml-1">{field.label}</label>
                         <ValidatedInput 
                            type="number" 
                            name={field.key}
                            value={formData[field.key]} 
                            onChange={(e) => setFormData({...formData, [field.key]: e.target.value})}
                            step={field.step || "1"}
                            lang={isRTL ? 'ar' : 'en'}
                         />
                     </div>
                 ))}
             </div>
             


             <button 
                onClick={handleRecommendation} 
                disabled={loadingCrops} 
                className="w-full mt-auto bg-[#C47A2C] hover:bg-[#A36322] text-white font-bold py-4 rounded-xl shadow-[max(0px,inset_0_-2px_4px_rgba(0,0,0,0.1))] transition-transform transform hover:-translate-y-0.5 active:scale-[0.99] disabled:opacity-50 disabled:hover:translate-y-0 flex items-center justify-center gap-2"
             >
                {loadingCrops ? <Activity size={20} className="animate-spin" /> : <Wheat size={20} />}
                {loadingCrops ? (t.crops.processing || "Processing...") : (t.crops.btn || "Get Recommendations")}
             </button>
        </div>
      </div>
        
      {/* RIGHT: RESULTS (Moved correctly into split pane domain) */}
      <div className="w-full lg:w-2/3 flex flex-col">
          {(!isStreaming && !isComplete && !hasError && !loadingCrops) ? (
             <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 border border-[#1B263B]/5 dark:border-slate-700 shadow-sm flex-1 flex flex-col items-center justify-center transition-colors">
                <div className="w-24 h-24 rounded-full bg-gray-50 dark:bg-slate-700/50 flex items-center justify-center mb-4">
                  <Wheat size={48} className="text-gray-300 dark:text-slate-500" />
                </div>
                <p className="text-[#1B263B]/50 dark:text-white/50 font-medium">{t.crops.results || "Submit parameters to generate optimal crop yields."}</p>
             </div>
          ) : (
             <DiagnosticsPanel
                isStreaming={isStreaming}
                streamContent={streamContent}
                thinkingContent={thinkingContent}
                isThinking={isThinking}
                isComplete={isComplete}
                hasError={hasError}
                errorMessage={errorMessage}
                onRetry={handleRecommendation}
                onNewAnalysis={() => { setStreamContent(''); setThinkingContent(''); setIsComplete(false); }}
                t={t}
                isRTL={isRTL}
             />
          )}
      </div>
      </div>
    </div>
  );
};

export default CropRecommendView;
